/**
 * Haven Seconds Kit Builder
 * Self-contained vanilla ES6+ kit builder for Shopify.
 * Renders a 3-step configurator: Tent Body (grade + color), Rainfly (color),
 * Pad (model). Hooks into the native product-form Add to Cart button to add
 * all three selected variants at once.
 *
 * Architecture mirrors zenbivy-bundle-builder.js.
 */

(function () {
  'use strict';

  const GRADE_DESCRIPTIONS = {
    'A': 'Like new. Minor cosmetic marks only.',
    'B': 'Shows use. May include minor repairs.',
  };

  class HavenSecondsBuilder {
    constructor(el) {
      this.el = el;
      this.stepsEl = el.querySelector('#hsb-steps');
      this.footerEl = el.querySelector('#hsb-footer');
      this.summaryEl = el.querySelector('#hsb-summary');

      // Labels from data attributes
      this.bodyLabel = el.dataset.bodyLabel || 'Step 1';
      this.flyLabel  = el.dataset.flyLabel  || 'Step 2';
      this.padLabel  = el.dataset.padLabel  || 'Step 3';

      // Products array: [body, fly, pad]
      this.products = [];

      // Selected options per step.
      //   Body (index 0): { 0: colorValue, 1: gradeValue }
      //   Fly  (index 1): { 0: colorValue }
      //   Pad  (index 2): { 0: modelValue }
      this.selectedOptions = [];

      // Resolved variant per step (null until both/all axes selected)
      this.selectedVariants = [];

      // Display option indices for fly and pad — determined dynamically after
      // filterProductVariants() so variant ID filtering is already applied.
      this.flyOptIdx = 0;
      this.padOptIdx = 0;

      // Guard against double-submission
      this._isAdding = false;

      this.init();
    }

    // ─── Init ──────────────────────────────────────────────────────────────

    async init() {
      // Claim the Add-to-Cart button immediately — before any async product
      // fetch — so clicking early can never trigger native form submission.
      this._claimButtonEarly();

      try {
        console.log('[HSB] init: fetchAllProducts...');
        await this.fetchAllProducts();
        console.log('[HSB] init: products =', JSON.stringify(this.products && this.products.map(p => p ? {title: p.title, variants: p.variants && p.variants.length} : null)));
        this.filterProductVariants();
        console.log('[HSB] init: renderSteps...');
        this.renderSteps();
        console.log('[HSB] init: hookNativeButton...');
        this.hookNativeButton();
        if (!this.isKitPurchasable()) {
          this.applyKitOosState();
        }
        console.log('[HSB] init: done.');
      } catch (err) {
        this.stepsEl.innerHTML =
          '<div class="hsb-loading">Error: ' + this.escapeHtml(err.message || String(err)) + '</div>';
        console.error('[HSB] init error:', err);
      }
    }

    // ─── Fetch ─────────────────────────────────────────────────────────────

    async fetchAllProducts() {
      // Prefer Liquid-pre-rendered data embedded in the page.
      const dataEl = document.getElementById('hsb-products-data');
      if (dataEl) {
        let parsed;
        try {
          parsed = JSON.parse(dataEl.textContent);
        } catch (jsonErr) {
          console.error('[HSB] JSON parse failed. Raw content:', dataEl.textContent.slice(0, 500));
          throw new Error('Product data JSON invalid: ' + jsonErr.message);
        }
        this.products = parsed.map(p => this._normalizeProduct(p));
        console.log('[HSB] pre-rendered products loaded:', this.products.map(p => p ? p.title : null));
        return;
      }

      // Fallback: fetch from the Shopify storefront API.
      // Keep positional slots (null for absent steps) so indices stay stable.
      const allHandles = [
        this.el.dataset.body,
        this.el.dataset.fly,
        this.el.dataset.pad,
      ];

      if (!allHandles.some(Boolean)) return;

      const fetches = allHandles.map((handle) => {
        if (!handle) return Promise.resolve(null);
        return fetch(`/products/${handle}.js`).then((r) => {
          if (!r.ok) throw new Error(`Failed to fetch product: ${handle}`);
          return r.json();
        });
      });
      this.products = (await Promise.all(fetches)).map(p => this._normalizeProduct(p));
    }

    // ─── Variant filter ────────────────────────────────────────────────────
    // Trims each product's variant list to only the IDs specified in the
    // corresponding data-*-variant-ids attribute. If the attribute is blank,
    // all variants are kept unchanged.

    filterProductVariants() {
      const keys = ['body', 'fly', 'pad'];
      keys.forEach((key, index) => {
        if (index >= this.products.length) return;

        if (!this.products[index]) return;
        const raw = this.el.dataset[key + 'VariantIds'];
        if (!raw || !raw.trim()) return;

        const allowedIds = raw
          .split(',')
          .map((s) => parseInt(s.trim(), 10))
          .filter(Boolean);

        if (!allowedIds.length) return;

        this.products[index] = Object.assign({}, this.products[index], {
          variants: this.products[index].variants.filter((v) =>
            allowedIds.includes(v.id)
          ),
        });
      });
    }

    // Returns the option index to display for a fly or pad step.
    // Prefers an option explicitly named "Color", then falls back to the first
    // option that has more than one distinct value across the (filtered) variants.
    // This handles products where Color is option2 (e.g. Size / Color).
    findDisplayOptionIndex(product) {
      const colorIdx = product.options.findIndex(
        (o) => o.toLowerCase() === 'color'
      );
      if (colorIdx !== -1) return colorIdx;

      for (let i = 0; i < product.options.length; i++) {
        if (this.getOptionValues(product, i).length > 1) return i;
      }
      return 0;
    }

    // ─── Render ────────────────────────────────────────────────────────────

    renderSteps() {
      this.stepsEl.innerHTML = '';
      const labels = [this.bodyLabel, this.flyLabel, this.padLabel];

      this.products.forEach((product, index) => {
        this.selectedOptions[index] = {};
        this.selectedVariants[index] = null;

        if (!product) return; // step absent (e.g. no fly on Safari)

        let stepEl;
        if (index === 0) {
          stepEl = this.buildBodyStep(product, labels[0]);
        } else if (index === 1) {
          this.flyOptIdx = this.findDisplayOptionIndex(product);
          stepEl = this.buildFlyStep(product, labels[1]);
        } else {
          this.padOptIdx = this.findDisplayOptionIndex(product);
          stepEl = this.buildPadStep(product, labels[2]);
        }

        this.stepsEl.appendChild(stepEl);
        this.autoSelectFirstAvailable(product, index);
      });

      this.refreshAllSoldOutStates();
      this.updateSummary();
    }

    // ─── Step builders ──────────────────────────────────────────────────────

    buildStepHead(label, index) {
      // Visual number = count of present (non-null) steps up to and including this one
      const stepNum = this.products.slice(0, index + 1).filter(Boolean).length;
      const head = document.createElement('div');
      head.className = 'hsb-step-head';
      head.innerHTML = `
        <span class="hsb-step-num">${stepNum}</span>
        <span class="hsb-step-name">${this.escapeHtml(label)}</span>
      `;
      return head;
    }

    buildBodyStep(product, label) {
      const step = document.createElement('div');
      step.className = 'hsb-step';
      step.dataset.stepIndex = '0';

      step.appendChild(this.buildStepHead(label, 0));

      const body = document.createElement('div');
      body.className = 'hsb-step-body';

      // Grade-only products (e.g. Haven Spectre) have a single option axis:
      // grade at index 0. Two-option products have color at 0, grade at 1.
      const gradeOnly = this._bodyIsGradeOnly(product);
      const gradeOptIdx = gradeOnly ? 0 : 1;

      // Grade group
      const gradeValues = this.getOptionValues(product, gradeOptIdx);
      const gradeGroup = document.createElement('div');
      gradeGroup.className = 'hsb-option-group';
      gradeGroup.dataset.optionIndex = String(gradeOptIdx);

      const gradeLabel = document.createElement('div');
      gradeLabel.className = 'hsb-option-label';
      gradeLabel.textContent = 'Grade';
      gradeGroup.appendChild(gradeLabel);
      gradeGroup.appendChild(this.buildGradePills(gradeValues, gradeOptIdx));
      body.appendChild(gradeGroup);

      // Color group — only present when the body has a color option
      if (!gradeOnly) {
        const colorValues = this.getOptionValues(product, 0);
        const colorGroup = document.createElement('div');
        colorGroup.className = 'hsb-option-group';
        colorGroup.dataset.optionIndex = '0';

        const colorLabel = document.createElement('div');
        colorLabel.className = 'hsb-option-label';
        colorLabel.textContent = 'Color';
        colorGroup.appendChild(colorLabel);
        colorGroup.appendChild(this.buildColorPills(colorValues, 0, 0));
        body.appendChild(colorGroup);
      }

      // Seconds note
      const note = document.createElement('div');
      note.className = 'hsb-seconds-note';
      note.innerHTML =
        'Grade applies to the tent body only. Fly and pad are cosmetic seconds — fully functional, may show signs of use like dirt, minor marks, or occasional campfire scent.';
      body.appendChild(note);

      // OOS message
      const oosMsg = document.createElement('div');
      oosMsg.className = 'hsb-oos-msg';
      oosMsg.style.display = 'none';
      oosMsg.dataset.oosStep = '0';
      body.appendChild(oosMsg);

      // Selection chips
      const chips = document.createElement('div');
      chips.className = 'hsb-selection-chips';
      body.appendChild(chips);

      step.appendChild(body);
      return step;
    }

    buildFlyStep(product, label) {
      const step = document.createElement('div');
      step.className = 'hsb-step';
      step.dataset.stepIndex = '1';

      step.appendChild(this.buildStepHead(label, 1));

      const body = document.createElement('div');
      body.className = 'hsb-step-body';

      // Color group — index resolved dynamically (product may have Size before Color)
      const colorValues = this.getOptionValues(product, this.flyOptIdx);
      const colorGroup = document.createElement('div');
      colorGroup.className = 'hsb-option-group';
      colorGroup.dataset.optionIndex = String(this.flyOptIdx);

      const colorLabel = document.createElement('div');
      colorLabel.className = 'hsb-option-label';
      colorLabel.textContent = 'Color';
      colorGroup.appendChild(colorLabel);
      colorGroup.appendChild(this.buildColorPills(colorValues, this.flyOptIdx, 1));
      body.appendChild(colorGroup);

      // OOS message
      const oosMsg = document.createElement('div');
      oosMsg.className = 'hsb-oos-msg';
      oosMsg.style.display = 'none';
      oosMsg.dataset.oosStep = '1';
      body.appendChild(oosMsg);

      // Selection chips
      const chips = document.createElement('div');
      chips.className = 'hsb-selection-chips';
      body.appendChild(chips);

      step.appendChild(body);
      return step;
    }

    buildPadStep(product, label) {
      const step = document.createElement('div');
      step.className = 'hsb-step';
      step.dataset.stepIndex = '2';

      step.appendChild(this.buildStepHead(label, 2));

      const body = document.createElement('div');
      body.className = 'hsb-step-body';

      // Pad model group — index resolved dynamically
      const padValues = this.getOptionValues(product, this.padOptIdx);
      const padGroup = document.createElement('div');
      padGroup.className = 'hsb-option-group';
      padGroup.dataset.optionIndex = String(this.padOptIdx);

      const padLabel = document.createElement('div');
      padLabel.className = 'hsb-option-label';
      padLabel.textContent = 'Model';
      padGroup.appendChild(padLabel);
      padGroup.appendChild(this.buildPadCardPills(product, padValues));
      body.appendChild(padGroup);

      // OOS message
      const oosMsg = document.createElement('div');
      oosMsg.className = 'hsb-oos-msg';
      oosMsg.style.display = 'none';
      oosMsg.dataset.oosStep = '2';
      body.appendChild(oosMsg);

      // Selection chips
      const chips = document.createElement('div');
      chips.className = 'hsb-selection-chips';
      body.appendChild(chips);

      step.appendChild(body);
      return step;
    }

    // ─── Pill builders ──────────────────────────────────────────────────────

    buildGradePills(values, optionIndex = 1) {
      const container = document.createElement('div');
      container.className = 'hsb-grade-pills';

      values.forEach((value) => {
        const desc = GRADE_DESCRIPTIONS[value] || '';
        const pill = document.createElement('div');
        pill.className = 'hsb-grade-pill';
        pill.dataset.optionIndex = String(optionIndex);
        pill.dataset.value = value;
        pill.setAttribute('role', 'button');
        pill.setAttribute('tabindex', '0');

        pill.innerHTML = `
          <span class="grade-label">${this.escapeHtml(value)} Grade</span>
          <div class="grade-title">${this.escapeHtml(value)} Grade</div>
          <div class="grade-desc">${this.escapeHtml(desc)}</div>
        `;

        const handleClick = () => this.onBodyOptionSelect(optionIndex, value, pill);
        pill.addEventListener('click', handleClick);
        pill.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        });

        container.appendChild(pill);
      });

      return container;
    }

    buildColorPills(values, optionIndex, stepIndex) {
      const container = document.createElement('div');
      container.className = 'hsb-color-pills';

      values.forEach((value) => {
        const pill = document.createElement('button');
        pill.className = 'hsb-color-pill';
        pill.type = 'button';
        pill.dataset.optionIndex = String(optionIndex);
        pill.dataset.value = value;

        const swatchClass = 'hsb-swatch-' + value.toLowerCase().replace(/\s+/g, '-');
        pill.innerHTML = `<span class="hsb-swatch ${this.escapeHtml(swatchClass)}"></span>${this.escapeHtml(value)}`;

        if (stepIndex === 0) {
          pill.addEventListener('click', () => this.onBodyOptionSelect(0, value, pill));
        } else {
          pill.addEventListener('click', () => this.onSingleOptionSelect(stepIndex, optionIndex, value, pill));
        }

        container.appendChild(pill);
      });

      return container;
    }

    buildPadCardPills(product, values) {
      const container = document.createElement('div');
      container.className = 'hsb-card-pills';

      values.forEach((value) => {
        const pill = document.createElement('div');
        pill.className = 'hsb-card-pill';
        pill.dataset.optionIndex = String(this.padOptIdx);
        pill.dataset.value = value;
        pill.setAttribute('role', 'button');
        pill.setAttribute('tabindex', '0');

        pill.innerHTML = `
          <div class="pad-name">${this.escapeHtml(value)}</div>
        `;

        const handleClick = () => this.onSingleOptionSelect(2, this.padOptIdx, value, pill);
        pill.addEventListener('click', handleClick);
        pill.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        });

        container.appendChild(pill);
      });

      return container;
    }

    // ─── Option Selection ───────────────────────────────────────────────────

    // Handles grade (optionIndex=1) and color (optionIndex=0) for the body step.
    onBodyOptionSelect(optionIndex, value, pillEl) {
      this.selectedOptions[0][optionIndex] = value;

      const step = this.stepsEl.querySelector('[data-step-index="0"]');
      const group = step.querySelector(`[data-option-index="${optionIndex}"]`);
      group.querySelectorAll('.hsb-grade-pill, .hsb-color-pill').forEach((p) => {
        p.classList.remove('selected');
      });
      pillEl.classList.add('selected');

      // Resolve body variant — requires both color (index 0) and grade (index 1)
      const variant = this.findBodyVariant(this.selectedOptions[0]);
      this.selectedVariants[0] = variant;

      this.updateStepPrice(0, variant);
      this.updateOosMessage(0, variant);
      this.refreshSoldOutStates(0);
      this.updateSummary();
      this.updateSelectionChips(0);
    }

    // Handles single-option steps: fly (stepIndex=1) and pad (stepIndex=2).
    onSingleOptionSelect(stepIndex, optionIndex, value, pillEl) {
      this.selectedOptions[stepIndex][optionIndex] = value;

      const step = this.stepsEl.querySelector(`[data-step-index="${stepIndex}"]`);
      const group = step.querySelector(`[data-option-index="${optionIndex}"]`);
      group.querySelectorAll('.hsb-color-pill, .hsb-card-pill').forEach((p) => {
        p.classList.remove('selected');
      });
      pillEl.classList.add('selected');

      const product = this.products[stepIndex];
      const variant = this.findMatchingVariant(product, this.selectedOptions[stepIndex]);
      this.selectedVariants[stepIndex] = variant;

      this.updateStepPrice(stepIndex, variant);
      this.updateOosMessage(stepIndex, variant);
      this.refreshSoldOutStates(stepIndex);
      this.updateSummary();
      this.updateSelectionChips(stepIndex);
    }

    // ─── Variant Matching ───────────────────────────────────────────────────

    // Body requires both option1 (color) and option2 (grade) to resolve,
    // unless the body is grade-only (single option), in which case only grade matters.
    findBodyVariant(selectedOptions) {
      if (this._bodyIsGradeOnly(this.products[0])) {
        const grade = selectedOptions[0];
        if (!grade) return null;
        return this.products[0].variants.find((v) => v.option1 === grade) || null;
      }
      const color = selectedOptions[0];
      const grade = selectedOptions[1];
      if (!color || !grade) return null;
      return (
        this.products[0].variants.find(
          (v) => v.option1 === color && v.option2 === grade
        ) || null
      );
    }

    // Returns true when the body product has only one option axis (no color).
    // Detected by product.options.length — grade-only products like Haven Spectre
    // have a single Grade option; all others have Color + Grade.
    _bodyIsGradeOnly(product) {
      return product && product.options.length === 1;
    }

    // Generic single-pass variant finder (used for fly, pad, and sold-out checks).
    findMatchingVariant(product, selectedOptions) {
      return (
        product.variants.find((v) => {
          return product.options.every((_, i) => {
            const key = `option${i + 1}`;
            return selectedOptions[i] === undefined || v[key] === selectedOptions[i];
          });
        }) || null
      );
    }

    // ─── Auto-select first available ────────────────────────────────────────

    autoSelectFirstAvailable(product, stepIndex) {
      const firstAvailable =
        product.variants.find((v) => v.available) || product.variants[0];
      if (!firstAvailable) return;

      const step = this.stepsEl.querySelector(`[data-step-index="${stepIndex}"]`);
      if (!step) return;

      if (stepIndex === 0) {
        if (this._bodyIsGradeOnly(product)) {
          // Grade-only body (e.g. Haven Spectre): single Grade option at index 0
          this.selectedOptions[0][0] = firstAvailable.option1;

          const gradeGroup = step.querySelector('[data-option-index="0"]');
          if (gradeGroup) {
            gradeGroup.querySelectorAll('.hsb-grade-pill').forEach((p) => {
              if (p.dataset.value === firstAvailable.option1) p.classList.add('selected');
            });
          }
        } else {
          // Normal body: color at index 0, grade at index 1
          this.selectedOptions[0][0] = firstAvailable.option1;
          this.selectedOptions[0][1] = firstAvailable.option2;

          const colorGroup = step.querySelector('[data-option-index="0"]');
          if (colorGroup) {
            colorGroup.querySelectorAll('.hsb-color-pill').forEach((p) => {
              if (p.dataset.value === firstAvailable.option1) p.classList.add('selected');
            });
          }

          const gradeGroup = step.querySelector('[data-option-index="1"]');
          if (gradeGroup) {
            gradeGroup.querySelectorAll('.hsb-grade-pill').forEach((p) => {
              if (p.dataset.value === firstAvailable.option2) p.classList.add('selected');
            });
          }
        }

        this.selectedVariants[0] = firstAvailable;
        this.updateStepPrice(0, firstAvailable);
        this.updateOosMessage(0, firstAvailable);
        this.updateSelectionChips(0);
      } else {
        // Fly (1) and Pad (2): single displayed option, index resolved dynamically
        const optIdx = stepIndex === 1 ? this.flyOptIdx : this.padOptIdx;
        const optKey = `option${optIdx + 1}`;
        this.selectedOptions[stepIndex][optIdx] = firstAvailable[optKey];

        const group = step.querySelector(`[data-option-index="${optIdx}"]`);
        if (group) {
          group.querySelectorAll('.hsb-color-pill, .hsb-card-pill').forEach((p) => {
            if (p.dataset.value === firstAvailable[optKey]) p.classList.add('selected');
          });
        }

        this.selectedVariants[stepIndex] = firstAvailable;
        this.updateStepPrice(stepIndex, firstAvailable);
        this.updateOosMessage(stepIndex, firstAvailable);
        this.updateSelectionChips(stepIndex);
      }
    }

    // ─── Sold-out state ─────────────────────────────────────────────────────

    refreshAllSoldOutStates() {
      this.products.forEach((p, i) => { if (p) this.refreshSoldOutStates(i); });
    }

    refreshSoldOutStates(stepIndex) {
      const product = this.products[stepIndex];
      const step = this.stepsEl.querySelector(`[data-step-index="${stepIndex}"]`);
      if (!step) return;

      step.querySelectorAll('.hsb-option-group').forEach((group) => {
        const optIdx = parseInt(group.dataset.optionIndex, 10);

        group.querySelectorAll('.hsb-grade-pill, .hsb-color-pill, .hsb-card-pill').forEach((pill) => {
          const value = pill.dataset.value;

          // Build testOptions: current selections + this candidate value
          const testOptions = Object.assign({}, this.selectedOptions[stepIndex], {
            [optIdx]: value,
          });

          // Check if any available variant matches this combination
          const hasAvailable = product.variants.some((v) => {
            const matches = product.options.every((_, i) => {
              const key = `option${i + 1}`;
              return testOptions[i] === undefined || v[key] === testOptions[i];
            });
            return matches && v.available;
          });

          if (hasAvailable) {
            pill.classList.remove('sold-out');
          } else {
            pill.classList.add('sold-out');
          }
        });
      });
    }

    // ─── Price helpers ──────────────────────────────────────────────────────

    getPriceRange(product) {
      const prices = product.variants.map((v) => v.price);
      const min = Math.min(...prices);
      return `from ${this.formatPrice(min)}`;
    }

    updateStepPrice(stepIndex, variant) {
      const priceEl = this.stepsEl.querySelector(`[data-step-price="${stepIndex}"]`);
      if (!priceEl) return;
      priceEl.textContent = variant
        ? this.formatPrice(variant.price)
        : this.getPriceRange(this.products[stepIndex]);
    }

    formatPrice(cents) {
      return (cents / 100).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      });
    }

    // ─── OOS message ────────────────────────────────────────────────────────

    updateOosMessage(stepIndex, variant) {
      const msgEl = this.stepsEl.querySelector(`[data-oos-step="${stepIndex}"]`);
      if (!msgEl) return;
      if (variant && !variant.available) {
        msgEl.textContent = 'Currently out of stock';
        msgEl.style.display = 'block';
      } else {
        msgEl.style.display = 'none';
      }
    }

    // ─── Summary ────────────────────────────────────────────────────────────

    // ─── Kit-level availability ─────────────────────────────────────────────

    // Returns false if any present product step has zero available variants
    // (after variant ID filtering). When false the entire kit cannot be purchased.
    isKitPurchasable() {
      return this.products.every((product) => {
        if (!product) return true; // absent steps (e.g. no fly) don't block the kit
        return product.variants.some((v) => v.available);
      });
    }

    // Called when isKitPurchasable() returns false. Grays out the step area,
    // shows an out-of-stock banner, hides the footer, and disables the ATC button.
    applyKitOosState() {
      this._kitUnavailable = true;

      // Add class so CSS can gray out the steps region
      this.el.classList.add('hsb--kit-unavailable');

      // Insert OOS banner between the header strip and the steps
      const banner = document.createElement('div');
      banner.className = 'hsb-kit-oos-banner';
      banner.textContent =
        'This kit is currently out of stock. Please check back soon.';
      this.stepsEl.insertAdjacentElement('beforebegin', banner);

      // Keep the footer hidden regardless of selection state
      this.footerEl.style.display = 'none';

      // Disable the Add to Cart button so the form cannot be submitted
      if (this._nativeBtn) {
        this._nativeBtn.disabled = true;
        this._nativeBtn.setAttribute('aria-disabled', 'true');
      }

      console.log('[HSB] kit is unavailable — OOS state applied');
    }

    updateSummary() {
      // If the entire kit is unavailable keep the footer hidden
      if (this._kitUnavailable) return;

      let total = 0;

      // A kit is fully selected when every present product slot has a chosen variant.
      const allSelected = this.products.every(
        (p, i) => !p || (this.selectedVariants[i] !== null && this.selectedVariants[i] !== undefined)
      );

      // Accumulate total from all selected variants
      this.selectedVariants.forEach((v) => { if (v) total += v.price; });

      if (allSelected) {
        this.summaryEl.innerHTML = `
          <div class="hsb-summary-line total">
            <span class="label">Kit Total</span>
            <span class="price">${this.formatPrice(total)}</span>
          </div>
        `;
        this.footerEl.style.display = 'block';
        this.updatePagePrice(total);
      } else {
        this.summaryEl.innerHTML = '';
        this.footerEl.style.display = 'none';
      }
    }

    updatePagePrice(totalCents) {
      const priceEl = document.querySelector('[data-hsb-page-price]');
      if (!priceEl) return;
      priceEl.innerHTML = `
        <div class="price price--large">
          <div class="price__container">
            <div class="price__regular">
              <span class="visually-hidden visually-hidden--inline">Regular price</span>
              <span class="price-item price-item--regular">${this.formatPrice(totalCents)}</span>
            </div>
          </div>
        </div>
      `;
    }

    // ─── Selection chips ────────────────────────────────────────────────────

    updateSelectionChips(stepIndex) {
      const step = this.stepsEl.querySelector(`[data-step-index="${stepIndex}"]`);
      if (!step) return;
      const container = step.querySelector('.hsb-selection-chips');
      if (!container) return;

      container.innerHTML = '';

      if (stepIndex === 0) {
        const gradeOnly = this._bodyIsGradeOnly(this.products[0]);
        // Grade-only: grade is at index 0. Normal: grade at 1, color at 0.
        const grade = gradeOnly ? this.selectedOptions[0][0] : this.selectedOptions[0][1];
        const color = gradeOnly ? null : this.selectedOptions[0][0];
        if (grade) {
          const chip = document.createElement('span');
          chip.className = 'hsb-chip';
          chip.textContent = grade + ' Grade';
          container.appendChild(chip);
        }
        if (color) {
          const chip = document.createElement('span');
          chip.className = 'hsb-chip';
          chip.textContent = color;
          container.appendChild(chip);
        }
      } else {
        // Fly and Pad: show option chip + price chip
        const optIdx = stepIndex === 1 ? this.flyOptIdx : this.padOptIdx;
        const val = this.selectedOptions[stepIndex][optIdx];
        const variant = this.selectedVariants[stepIndex];
        if (val) {
          const chip = document.createElement('span');
          chip.className = 'hsb-chip';
          chip.textContent = val;
          container.appendChild(chip);
        }
      }
    }

    // ─── Native button hook ─────────────────────────────────────────────────

    hookNativeButton() {
      // _claimButtonEarly() already found and converted the button before the
      // async product fetch. If it ran successfully, just attach the listener.
      if (!this._nativeBtn) {
        const productFormEl = document.querySelector('product-form');
        console.log('[HSB] hookNativeButton: product-form found?', !!productFormEl);
        if (!productFormEl) return;

        // At this point the button may already be type="button" if _claimButtonEarly
        // ran, so also check for .product-form__submit as fallback.
        const submitBtn =
          productFormEl.querySelector('[type="submit"]') ||
          productFormEl.querySelector('.product-form__submit');
        console.log('[HSB] hookNativeButton: submit button found?', !!submitBtn);
        if (!submitBtn) return;

        this._nativeBtn = submitBtn;
        this._productFormEl = productFormEl;
        submitBtn.type = 'button';
      }

      this._nativeBtn.addEventListener('click', () => {
        console.log('[HSB] submit button CLICK intercepted');
        this.handleKitAdd();
      });

      // Ensure form submit is blocked even if _claimButtonEarly ran before the
      // product-form element was fully connected (edge case: slow hydration).
      this._blockNativeFormSubmit(this._productFormEl);
    }

    // Claim the Add-to-Cart button immediately (before any async work) so that
    // an early click cannot fall through to the native form submission path.
    _claimButtonEarly() {
      const productFormEl = document.querySelector('product-form');
      if (!productFormEl) return;

      const submitBtn =
        productFormEl.querySelector('[type="submit"]') ||
        productFormEl.querySelector('.product-form__submit');
      if (!submitBtn) return;

      this._nativeBtn = submitBtn;
      this._productFormEl = productFormEl;
      submitBtn.type = 'button';

      // Belt-and-suspenders: also intercept the native form's submit event in
      // the capture phase so that third-party scripts, keyboard Enter, or any
      // other trigger can never cause the main product to be submitted to cart.
      this._blockNativeFormSubmit(productFormEl);

      console.log('[HSB] _claimButtonEarly: button claimed');
    }

    // Collect any currently-selected upsell / bundle items from pdp-upsell-v2
    // so they can be merged into the same /cart/add.js request as the kit items.
    collectUpsellItems() {
      const upsellEl = document.querySelector('pdp-upsell-v2');
      if (!upsellEl) return [];

      const items = [];

      // Individual upsell product checkboxes
      upsellEl.querySelectorAll('.pdp-upsell-v2__checkbox:checked').forEach((cb) => {
        const variantId = parseInt(cb.dataset.variantId, 10);
        if (variantId) items.push({ id: variantId, quantity: 1 });
      });

      // Bundle radio (only one can be selected at a time)
      const bundleRadio = upsellEl.querySelector('.pdp-upsell-v2__bundle-radio:checked');
      if (bundleRadio) {
        const variantId = parseInt(bundleRadio.dataset.variantId, 10);
        if (variantId) items.push({ id: variantId, quantity: 1 });
      }

      console.log('[HSB] collectUpsellItems:', items);
      return items;
    }

    async handleKitAdd() {
      console.log('[HSB] handleKitAdd called, _isAdding:', this._isAdding);
      if (this._kitUnavailable) return;
      if (this._isAdding) return;
      this._isAdding = true;

      const btn = this._nativeBtn;
      const spinner = this._productFormEl
        ? this._productFormEl.querySelector('.loading-overlay__spinner')
        : null;

      btn.setAttribute('aria-disabled', 'true');
      btn.classList.add('loading');
      if (spinner) spinner.classList.remove('hidden');
      this._clearError();

      try {
        const items = this.selectedVariants
          .filter((v) => v !== null && v !== undefined)
          .map((v) => ({ id: v.id, quantity: 1 }));

        // Merge any selected upsell items into the same request.
        this.collectUpsellItems().forEach((u) => items.push(u));

        // Tag every item with a shared bundle ID so the cart drawer can group them.
        const bundleId = 'seconds-' + Date.now() + '-' + Math.random().toString(36).slice(2, 7);
        const bundleName = this.el.dataset.modelLabel || this.products[0]?.title || 'Haven Second';
        items.forEach((item) => {
          item.properties = { _hskit_id: bundleId, _hskit_label: bundleName };
        });

        console.log('[HSB] selectedVariants:', this.selectedVariants);
        console.log('[HSB] items to add:', items);

        if (!items.length) {
          console.log('[HSB] no items selected, aborting');
          return;
        }

        const addResponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items }),
        });
        console.log('[HSB] /cart/add.js status:', addResponse.status);

        if (!addResponse.ok) {
          const errData = await addResponse.json().catch(() => ({}));
          console.error('[HSB] cart add error:', errData);
          throw new Error(errData.description || 'Could not add items to cart.');
        }

        const addData = await addResponse.json();
        console.log('[HSB] /cart/add.js response:', addData);

        // Fetch updated section HTML and re-render the cart drawer.
        const cartEl =
          document.querySelector('cart-drawer') ||
          document.querySelector('cart-notification');

        if (cartEl && typeof cartEl.getSectionsToRender === 'function') {
          const sectionIds = cartEl.getSectionsToRender().map((s) => s.id);
          const sectionsResp = await fetch(
            window.location.pathname + '?sections=' + sectionIds.join(',')
          );
          if (sectionsResp.ok) {
            const sectionsData = await sectionsResp.json();
            cartEl.classList.remove('is-empty');
            cartEl.renderContents({ sections: sectionsData });
            return;
          }
        }

        if (cartEl && typeof cartEl.open === 'function') cartEl.open();
      } catch (err) {
        console.error('[HSB] handleKitAdd error:', err);
        this._showError(err.message || 'Something went wrong adding to cart. Please try again.');
      } finally {
        this._isAdding = false;
        btn.removeAttribute('aria-disabled');
        btn.classList.remove('loading');
        if (spinner) spinner.classList.add('hidden');
      }
    }

    // ─── Utility ────────────────────────────────────────────────────────────

    getOptionValues(product, optionIndex) {
      const seen = new Set();
      const values = [];
      product.variants.forEach((v) => {
        const val = v[`option${optionIndex + 1}`];
        if (val != null && !seen.has(val)) {
          seen.add(val);
          values.push(val);
        }
      });
      return values;
    }

    // Ensures product.options is always a plain JS array of strings regardless
    // of how Shopify's | json filter serialised it (array, keyed object, etc.).
    _normalizeProduct(p) {
      if (!p) return null;
      let opts = p.options;
      if (!Array.isArray(opts)) {
        opts = opts ? Object.values(opts) : [];
      }
      opts = opts.map(o => (typeof o === 'string' ? o : (o && o.name) ? o.name : String(o)));
      return Object.assign({}, p, { options: opts });
    }

    // Prevent the native Shopify product form from ever submitting while the
    // kit builder is active.  Called in both the early-claim and hook phases.
    _blockNativeFormSubmit(productFormEl) {
      if (!productFormEl) return;
      const nativeForm = productFormEl.querySelector('form');
      if (!nativeForm || nativeForm.dataset.hsbBlocked) return;
      nativeForm.dataset.hsbBlocked = 'true';
      // Capture phase runs before the ProductForm's bubble-phase submit listener.
      nativeForm.addEventListener('submit', function (e) {
        e.preventDefault();
        e.stopImmediatePropagation();
        console.log('[HSB] native form submit blocked');
      }, true);
    }

    // Surface a cart-add error in the native product-form error wrapper so the
    // customer sees useful feedback instead of a silent failure.
    _showError(message) {
      if (!this._productFormEl) return;
      const wrapper = this._productFormEl.querySelector('.product-form__error-message-wrapper');
      if (!wrapper) return;
      const span = wrapper.querySelector('.product-form__error-message');
      if (span) span.textContent = message;
      wrapper.removeAttribute('hidden');
    }

    _clearError() {
      if (!this._productFormEl) return;
      const wrapper = this._productFormEl.querySelector('.product-form__error-message-wrapper');
      if (wrapper) wrapper.setAttribute('hidden', '');
    }

    escapeHtml(str) {
      if (!str) return '';
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  }

  // ─── Bootstrap ──────────────────────────────────────────────────────────────

  function init() {
    const el = document.getElementById('haven-seconds-kit-builder');
    if (!el) return;

    if (el.dataset.hsbReady) return;
    el.dataset.hsbReady = 'true';

    new HavenSecondsBuilder(el);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function () {
    const el = document.getElementById('haven-seconds-kit-builder');
    if (!el) return;
    delete el.dataset.hsbReady;
    init();
  });

  document.addEventListener('shopify:block:select', function () {
    const el = document.getElementById('haven-seconds-kit-builder');
    if (!el) return;
    if (el.dataset.hsbReady) return;
    init();
  });
})();
